import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../../contexts/AuthContext';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { generateVCardData, downloadVCard } from '../../../utils/vcard';
import { FaUser, FaPhone, FaEnvelope, FaQrcode } from 'react-icons/fa';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { LightBulbIcon } from '@heroicons/react/24/solid';
import logoSvg from '../../../assets/images/logo.svg';
import qrIcon from '../../../assets/icons/icon.png';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose }) => {
  const { currentProfile } = useAuth();
  const { tier } = useSubscription();
  const [contactName, setContactName] = React.useState<string | null>(null);

  // Get the contact name from sessionStorage when the modal opens
  React.useEffect(() => {
    if (isOpen) {
      const storedContactName = sessionStorage.getItem('shareWithContactName');
      setContactName(storedContactName);

      // Clear the stored contact name when the modal is closed
      return () => {
        sessionStorage.removeItem('shareWithContactName');
      };
    }
  }, [isOpen]);

  if (!isOpen || !currentProfile?.name) return null;

  const vCardData = generateVCardData(currentProfile, tier);

  const handleShareDetails = async () => {
    if (!currentProfile?.name) return;

    const blob = new Blob([vCardData], { type: 'text/vcard;charset=utf-8' });
    const file = new File([blob], `${currentProfile.name}.vcf`, { type: 'text/vcard' });

    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: `Contact details for ${currentProfile.name}`,
          text: contactName
            ? `Contact details for ${currentProfile.name} shared with ${contactName}.\n\nGet your FREE SayCard now! https://say.cards`
            : `Contact details for ${currentProfile.name}.\n\nGet your FREE SayCard now! https://say.cards`
        });
      } catch (error) {
        if ((error as any)?.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      downloadVCard(currentProfile, tier);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <span className="sr-only">Close</span>
          <XMarkIcon className="w-6 h-6" />
        </button>

        <div className="flex items-center mb-8">
          <img src={logoSvg} alt="SayCard Logo" className="h-8" />
        </div>

        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-4">My Contact Details</h2>
          {contactName && (
            <p className="text-green-600 mb-4">
              Share your details with {contactName}
            </p>
          )}
          <div className="space-y-2">
            <div className="flex items-center">
              <FaUser className="w-5 h-5 text-gray-500 mr-2" />
              <p className="text-lg">{currentProfile.name}</p>
            </div>
            {currentProfile.phone && (
              <div className="flex items-center">
                <FaPhone className="w-5 h-5 text-gray-500 mr-2" />
                <p className="text-gray-600">{currentProfile.phone}</p>
              </div>
            )}
            {currentProfile.email && (
              <div className="flex items-center">
                <FaEnvelope className="w-5 h-5 text-gray-500 mr-2" />
                <p className="text-gray-600">{currentProfile.email}</p>
              </div>
            )}
          </div>
        </div>

        <button
          onClick={handleShareDetails}
          className="w-full bg-blue-600 text-white rounded-lg py-2 px-4 hover:bg-blue-700 mb-6 flex items-center justify-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          <span>Share Contact Info</span>
        </button>

        <div className="text-center">
          <div className="flex items-center justify-center mb-4">
            <FaQrcode className="w-5 h-5 text-gray-500 mr-2" />
            <p className="text-gray-600">Or scan this QR code</p>
          </div>
          <div className="inline-block bg-white p-2 rounded-lg shadow-lg">
            <QRCodeSVG
              value={vCardData}
              size={200}
              level="M"
              imageSettings={{
                src: qrIcon,
                height: 48,
                width: 48,
                excavate: true,
              }}
            />
          </div>
        </div>

        <div className="mt-8">
          <div className="flex items-center mb-3">
            <LightBulbIcon className="w-5 h-5 text-yellow-500 mr-2" />
            <h3 className="font-bold text-gray-900">Pro Tips</h3>
          </div>
          <ul className="text-gray-600 space-y-2 text-sm">
            <li className="flex items-start">
              <span className="text-blue-500 mr-2">→</span>
              QR code works even without internet connection
            </li>
            <li className="flex items-start">
              <span className="text-blue-500 mr-2">→</span>
              Create more Share Profiles for different uses
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
